/**
 * Mobile navigation (screenshots/55, brief §2.9 "Mobile"): below 768px the rail becomes a bottom tab bar with the
 * user's first 4 tabs (same order as their sidebar, so Edit sidebar still controls it) + More. More opens the full
 * sidebar (views, utilities, Edit sidebar) as a bottom sheet; the Shell owns that state. On the admin dashboard the
 * Shell passes its items (navConfig ADMIN_TABS) instead.
 */
import { useMemo } from "react";
import { Menu } from "lucide-react";
import { adminTabOf, resolveSidebar, type AdminTabDef, type CustomView, type NavPrefs, type OrgRole, type UserGroup } from "../../nav/navConfig";
import { NavIcon } from "../../nav/Sidebar";
import { navigate, plainClick } from "../../app/router";
import { useFeaturesVersion } from "../../realtime/entitlements";

export function BottomTabBar({ group, role, prefs, teamViews, activePath, items, moreOpen, onMore }: {
  group: UserGroup; role: OrgRole | null; prefs: NavPrefs; teamViews?: CustomView[]; activePath: string;
  /** The admin dashboard's items in place of the user's tabs. */
  items?: AdminTabDef[];
  moreOpen: boolean; onMore: () => void;
}) {
  const features = useFeaturesVersion();   // the viewer's feature map changed (realtime/entitlements.ts)
  const sb = useMemo(() => (void features, resolveSidebar(group, role, prefs, teamViews)), [group, role, prefs, teamViews, features]);
  const first = items ?? sb.tabs.slice(0, 4);
  const adminActive = items ? adminTabOf(activePath) : null;
  const isActive = (path: string, id: string) => (items ? adminActive === id : path === "/dashboard" ? activePath === path : activePath.startsWith(path));
  return (
    <nav className="bottom-tabs" aria-label="Main">
      {first.map((t) => (
        <a key={t.id} href={t.path} aria-current={!moreOpen && isActive(t.path, t.id) ? "page" : undefined}
          onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); navigate(t.path); }}>
          <NavIcon name={t.icon} size={22} /><span>{t.label}</span>
        </a>
      ))}
      <button type="button" aria-expanded={moreOpen} aria-haspopup="dialog" data-on={moreOpen || undefined} onClick={onMore}>
        <Menu size={22} aria-hidden /><span>More</span>
      </button>
    </nav>
  );
}
